import { useEffect, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { gsap } from "@/lib/gsap";
import { useT } from "@/i18n";
import { LANDING_PAGES } from "@/data/landingPages";
import { SERVICES_LIST } from "@/data/services";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

/**
 * SEO long-tail landing page — frontend-only.
 * Renders editorial body + JSON-LD (Service + FAQPage) for discoverability.
 */
export default function LandingPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useT();

  const page = LANDING_PAGES.find((p) => p.slug === slug);
  const parent = page
    ? SERVICES_LIST.find((s) => s.slug === page.parentService)
    : null;

  // Title + meta — must run before JSON-LD so crawler sees it
  useEffect(() => {
    if (!page) return;
    const prevTitle = document.title;
    document.title = page.seo.title[lang] || page.seo.title.id;

    const setMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        document.head.appendChild(el);
      }
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      return el;
    };

    const desc = page.seo.description[lang] || page.seo.description.id;
    setMeta('meta[name="description"]', { name: "description", content: desc });
    setMeta('meta[name="keywords"]', { name: "keywords", content: page.seo.keywords });
    setMeta('meta[property="og:title"]', { property: "og:title", content: page.seo.title[lang] || page.seo.title.id });
    setMeta('meta[property="og:description"]', { property: "og:description", content: desc });
    setMeta('meta[property="og:type"]', { property: "og:type", content: "website" });

    return () => {
      // Restore the previous document title when leaving the landing page
      document.title = prevTitle;
    };
  }, [page, lang]);

  // JSON-LD: Service + FAQPage + BreadcrumbList
  const jsonLd = useMemo(() => {
    if (!page) return null;
    const origin = typeof window !== "undefined" ? window.location.origin : "https://makario.id";
    const url = `${origin}/jasa/${page.slug}`;
    const desc = page.seo.description[lang] || page.seo.description.id;
    return [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: page.h1[lang] || page.h1.id,
        description: desc,
        provider: {
          "@type": "Organization",
          name: "Makario",
          url: origin,
        },
        areaServed: { "@type": "Country", name: "Indonesia" },
        url,
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: page.faqs.map((f) => ({
          "@type": "Question",
          name: f.q[lang] || f.q.id,
          acceptedAnswer: {
            "@type": "Answer",
            text: f.a[lang] || f.a.id,
          },
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Makario", item: origin },
          { "@type": "ListItem", position: 2, name: lang === "id" ? "Layanan" : "Services", item: `${origin}/#services` },
          { "@type": "ListItem", position: 3, name: page.eyebrow[lang] || page.eyebrow.id, item: url },
        ],
      },
    ];
  }, [page, lang]);

  useEffect(() => {
    if (!page) return;
    window.scrollTo(0, 0);
    const ctx = gsap.context(() => {
      gsap.from("[data-anim='lp-eyebrow']", { opacity: 0, y: 14, duration: 0.6 });
      gsap.from("[data-anim='lp-h1']", { opacity: 0, y: 40, duration: 1.05, ease: "power3.out", delay: 0.1 });
      gsap.from("[data-anim='lp-sub']", { opacity: 0, y: 22, duration: 0.85, delay: 0.3 });
      gsap.from("[data-anim='lp-row']", {
        opacity: 0,
        y: 36,
        duration: 0.85,
        stagger: 0.14,
        ease: "power3.out",
        scrollTrigger: { trigger: "[data-anim='lp-body']", start: "top 80%" },
      });
    });
    return () => ctx.revert();
  }, [page]);

  if (!page) {
    return (
      <main className="min-h-screen flex items-center justify-center text-[var(--ink)]">
        <div className="text-center">
          <p className="h-sub">{t("detail.notFound")}</p>
          <button
            onClick={() => navigate("/")}
            data-testid="lp-back-home"
            className="btn-pill mt-8 text-[var(--ink)] border-[var(--ink)]"
          >
            <ArrowLeft size={14} /> {t("detail.back")}
          </button>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="landing-page" className="min-h-screen">
      {/* JSON-LD blocks for search engines */}
      {jsonLd?.map((block, i) => (
        <script
          key={i}
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(block) }}
        />
      ))}

      <Navbar />

      {/* HERO — minimal, restrained */}
      <section
        className="relative min-h-[88vh] flex flex-col px-6 sm:px-8 md:px-20 lg:px-32 pt-36 md:pt-40 pb-20 overflow-hidden grain"
        style={{ background: "var(--ink)" }}
      >
        <div
          className="absolute pointer-events-none rounded-full"
          style={{
            width: 1100,
            height: 1100,
            top: "-30%",
            right: "-25%",
            background: `radial-gradient(circle, ${page.accent} 0%, transparent 60%)`,
            opacity: 0.10,
            filter: "blur(50px)",
          }}
        />

        <div
          data-anim="lp-eyebrow"
          className="relative z-10 flex items-center justify-between mb-16 md:mb-24"
        >
          <Link
            to="/"
            data-testid="lp-back"
            className="inline-flex items-center gap-2 text-white/65 hover:text-white text-[0.62rem] md:text-[0.65rem] font-mono uppercase tracking-[0.28em] transition-colors"
          >
            <ArrowLeft size={14} /> Makario
          </Link>
          <div className="flex items-center gap-4 font-mono text-[0.6rem] uppercase tracking-[0.3em] text-white/45">
            <span>{lang === "id" ? "Layanan" : "Service"}</span>
            <span className="hidden md:inline">{page.eyebrow[lang]}</span>
          </div>
        </div>

        <div className="relative z-10 grid grid-cols-12 gap-x-6 md:gap-x-12 gap-y-8 items-center">
          <div className="col-span-12 md:col-span-2 flex items-center gap-3 self-start">
            <span className="inline-block h-px w-6" style={{ background: page.accent }} />
            <span className="font-mono text-[0.6rem] uppercase tracking-[0.32em] text-white/55">
              {page.eyebrow[lang]}
            </span>
          </div>

          <h1
            data-anim="lp-h1"
            data-testid="lp-h1"
            className="col-span-12 md:col-span-10 md:col-start-3 font-display text-white"
            style={{
              fontSize: "clamp(2rem, 5vw, 4.8rem)",
              lineHeight: 0.98,
              letterSpacing: "-0.035em",
              fontWeight: 600,
              maxWidth: "22ch",
            }}
          >
            {page.h1[lang]}
          </h1>

          <p
            data-anim="lp-sub"
            className="col-span-12 md:col-span-7 md:col-start-3 font-display italic text-white/85"
            style={{
              fontSize: "clamp(1rem, 1.5vw, 1.35rem)",
              lineHeight: 1.4,
              fontFamily: "Cormorant Garamond, serif",
              fontWeight: 500,
              color: page.accent,
              maxWidth: "40rem",
            }}
          >
            {page.sub[lang]}
          </p>
        </div>

        {/* Bottom rule */}
        <div className="relative z-10 mt-auto pt-10 border-t border-white/12 flex items-center justify-between font-mono text-[0.55rem] uppercase tracking-[0.28em] text-white/40">
          <span>Makario · Indonesia</span>
          <span>SEO landing · {page.slug}</span>
        </div>
      </section>

      {/* BODY */}
      <section
        className="relative px-6 sm:px-8 md:px-20 lg:px-32 py-28 md:py-40"
        style={{ background: "var(--paper)" }}
        data-anim="lp-body"
      >
        <div className="max-w-[1180px] mx-auto">
          {/* INTRO */}
          <div
            data-anim="lp-row"
            className="grid grid-cols-12 gap-x-6 md:gap-x-12 gap-y-6 mb-28 md:mb-36"
          >
            <div className="col-span-12 md:col-span-3 flex items-center gap-3 self-start">
              <span className="inline-block h-px w-6" style={{ background: page.accent }} />
              <span className="font-mono text-[0.6rem] uppercase tracking-[0.32em] text-black/45">
                01 · {lang === "id" ? "Konteks" : "Context"}
              </span>
            </div>
            <p
              className="col-span-12 md:col-span-9 text-black/70 leading-[1.85]"
              style={{ fontSize: "clamp(15px, 1.15vw, 18px)", maxWidth: "44rem" }}
            >
              {page.intro[lang]}
            </p>
          </div>

          {/* VALUE PROPS — editorial rows, no cards */}
          <div data-anim="lp-row" className="mb-28 md:mb-36">
            <div className="flex items-center gap-3 mb-8">
              <span className="inline-block h-px w-6" style={{ background: page.accent }} />
              <span className="font-mono text-[0.6rem] uppercase tracking-[0.32em] text-black/45">
                02 · {lang === "id" ? "Apa yang membuatnya berbeda" : "What makes it different"}
              </span>
            </div>
            <ol className="border-t border-black/10">
              {page.valueProps.map((vp, i) => (
                <li
                  key={i}
                  className="grid grid-cols-12 gap-x-6 md:gap-x-10 items-baseline py-6 md:py-7 border-b border-black/10"
                >
                  <span
                    className="col-span-2 md:col-span-1 font-mono text-[0.6rem] uppercase tracking-[0.28em] flex-none"
                    style={{ color: page.accent }}
                  >
                    /{String(i + 1).padStart(2, "0")}
                  </span>
                  <h3
                    className="col-span-10 md:col-span-4 font-display text-[var(--ink)]"
                    style={{
                      fontSize: "clamp(1.05rem, 1.7vw, 1.5rem)",
                      letterSpacing: "-0.018em",
                      fontWeight: 600,
                      lineHeight: 1.18,
                    }}
                  >
                    {vp.title[lang]}
                  </h3>
                  <p
                    className="col-span-12 md:col-span-7 mt-3 md:mt-0 text-black/65 leading-[1.7]"
                    style={{ fontSize: "clamp(14px, 1vw, 16px)" }}
                  >
                    {vp.desc[lang]}
                  </p>
                </li>
              ))}
            </ol>
          </div>

          {/* BEST FOR */}
          <div
            data-anim="lp-row"
            className="grid grid-cols-12 gap-x-6 md:gap-x-12 gap-y-6 mb-28 md:mb-36"
          >
            <div className="col-span-12 md:col-span-3 flex items-center gap-3 self-start">
              <span className="inline-block h-px w-6" style={{ background: page.accent }} />
              <span className="font-mono text-[0.6rem] uppercase tracking-[0.32em] text-black/45">
                03 · {lang === "id" ? "Cocok untuk" : "Best for"}
              </span>
            </div>
            <ul className="col-span-12 md:col-span-9 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-0">
              {page.bestFor[lang].map((b, i) => (
                <li
                  key={i}
                  className="flex items-baseline gap-4 py-4 border-b border-black/10"
                >
                  <span
                    className="font-mono text-[0.55rem] uppercase tracking-[0.28em] flex-none"
                    style={{ color: page.accent }}
                  >
                    +
                  </span>
                  <span className="text-[15px] text-black/75 leading-relaxed">
                    {b}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* FAQ */}
          <div data-anim="lp-row" className="mb-28 md:mb-36">
            <div className="flex items-center gap-3 mb-10">
              <span className="inline-block h-px w-6" style={{ background: page.accent }} />
              <span className="font-mono text-[0.6rem] uppercase tracking-[0.32em] text-black/45">
                04 · {lang === "id" ? "Pertanyaan yang sering ditanya" : "Frequently asked"}
              </span>
            </div>
            <dl className="border-t border-black/10">
              {page.faqs.map((f, i) => (
                <div
                  key={i}
                  data-testid={`lp-faq-${i}`}
                  className="grid grid-cols-12 gap-x-6 md:gap-x-10 py-7 md:py-8 border-b border-black/10"
                >
                  <dt
                    className="col-span-12 md:col-span-5 font-display text-[var(--ink)]"
                    style={{
                      fontSize: "clamp(1rem, 1.4vw, 1.25rem)",
                      letterSpacing: "-0.018em",
                      fontWeight: 600,
                      lineHeight: 1.25,
                    }}
                  >
                    {f.q[lang]}
                  </dt>
                  <dd
                    className="col-span-12 md:col-span-7 mt-3 md:mt-0 text-black/65 leading-[1.75]"
                    style={{ fontSize: "clamp(14px, 1vw, 16px)" }}
                  >
                    {f.a[lang]}
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          {/* CTA strip */}
          <div
            data-anim="lp-row"
            className="relative rounded-2xl overflow-hidden p-10 md:p-16 mb-24"
            style={{ background: "var(--ink)" }}
          >
            <div
              className="absolute pointer-events-none rounded-full"
              style={{
                width: 600,
                height: 600,
                top: "-45%",
                right: "-15%",
                background: `radial-gradient(circle, ${page.accent} 0%, transparent 60%)`,
                opacity: 0.16,
                filter: "blur(35px)",
              }}
            />
            <div className="relative flex flex-col md:flex-row gap-10 md:items-end md:justify-between">
              <div className="text-white max-w-[36rem]">
                <div className="flex items-center gap-3 mb-4">
                  <span className="inline-block h-px w-6" style={{ background: page.accent }} />
                  <span className="font-mono text-[0.6rem] uppercase tracking-[0.32em] text-white/55">
                    {lang === "id" ? "Diskusi" : "Discuss"}
                  </span>
                </div>
                <p
                  className="font-display text-white"
                  style={{
                    fontSize: "clamp(1.4rem,2.2vw,1.9rem)",
                    letterSpacing: "-0.022em",
                    fontWeight: 500,
                    lineHeight: 1.22,
                  }}
                >
                  {lang === "id"
                    ? "Mari diskusikan ruang lingkup yang tepat untuk konteks bisnis Anda."
                    : "Let's discuss what the right scope looks like for your business context."}
                </p>
              </div>
              <Link
                to="/#contact"
                data-testid="lp-cta"
                className="inline-flex items-center gap-3 self-start md:self-auto font-mono text-[0.62rem] md:text-[0.65rem] uppercase tracking-[0.28em] text-white border border-white/30 rounded-full px-5 py-3 hover:bg-white hover:text-black transition-all duration-300"
              >
                {t("nav.cta")} <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>

          {/* Parent discipline link + related landing pages */}
          <div data-anim="lp-row" className="mb-12">
            <div className="flex items-center gap-3 mb-8">
              <span className="inline-block h-px w-6 bg-black/40" />
              <span className="font-mono text-[0.6rem] uppercase tracking-[0.32em] text-black/45">
                05 · {lang === "id" ? "Pelajari Lebih Dalam" : "Go deeper"}
              </span>
            </div>
            <ol className="border-t border-black/10">
              {parent && (
                <li className="border-b border-black/10">
                  <Link
                    to={`/services/${parent.slug}`}
                    data-testid="lp-parent-service"
                    className="group flex items-center justify-between gap-6 py-5"
                  >
                    <div className="flex items-baseline gap-5 min-w-0">
                      <span className="font-mono text-[0.6rem] uppercase tracking-[0.28em] flex-none text-black/40">
                        {lang === "id" ? "Disiplin" : "Discipline"}
                      </span>
                      <span
                        className="font-display text-[var(--ink)] truncate transition-all duration-300 group-hover:translate-x-1"
                        style={{ fontSize: "clamp(1.05rem,1.7vw,1.4rem)", letterSpacing: "-0.015em", fontWeight: 600 }}
                      >
                        {parent.name}
                      </span>
                    </div>
                    <ArrowUpRight size={18} className="text-black/35 group-hover:text-[var(--violet)] transition-colors flex-none" />
                  </Link>
                </li>
              )}
              {LANDING_PAGES.filter((p) => p.slug !== page.slug)
                .slice(0, 3)
                .map((p) => (
                  <li key={p.slug} className="border-b border-black/10">
                    <Link
                      to={`/jasa/${p.slug}`}
                      data-testid={`lp-related-${p.slug}`}
                      className="group flex items-center justify-between gap-6 py-5"
                    >
                      <div className="flex items-baseline gap-5 min-w-0">
                        <span className="font-mono text-[0.6rem] uppercase tracking-[0.28em] flex-none" style={{ color: p.accent }}>
                          →
                        </span>
                        <span
                          className="font-display text-[var(--ink)] truncate transition-all duration-300 group-hover:translate-x-1"
                          style={{ fontSize: "clamp(1rem,1.6vw,1.3rem)", letterSpacing: "-0.015em", fontWeight: 500 }}
                        >
                          {p.eyebrow[lang]}
                        </span>
                      </div>
                      <ArrowUpRight size={18} className="text-black/35 group-hover:text-[var(--violet)] transition-colors flex-none" />
                    </Link>
                  </li>
                ))}
            </ol>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
